import React, { useEffect, useRef, useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import appSettings from '../../../../config/settings';
import { useIntl } from '../../../../util/reactIntl';
import { createInstance } from '../../../../util/sdkLoader';
import { isMainSearchTypeKeywords } from '../../../../util/search';
import { createSlug } from '../../../../util/urlHelpers';
import { cleanListingTitle } from '../../../../util/string';

import { Form, LocationAutocompleteInput } from '../../../../components';

import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';

const identity = v => v;
const LIVE_SEARCH_MIN_CHARS = 3;
const LIVE_SEARCH_DEBOUNCE_MS = 220;
const LIVE_SEARCH_LIMIT = 6;
const LIVE_SEARCH_MIN_STOCK = 1;
const LIVE_SEARCH_STOCK_MODE = 'match-undefined';

let sdkInstance;
const getSdk = () => {
  if (sdkInstance) {
    return sdkInstance;
  }

  const baseUrlMaybe = appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {};
  sdkInstance = createInstance({
    clientId: appSettings.sdk.clientId,
    ...baseUrlMaybe,
  });

  return sdkInstance;
};

const KeywordSearchField = props => {
  const {
    keywordSearchWrapperClasses,
    iconClass,
    intl,
    isMobile = false,
    inputRef,
    onKeywordTyping,
    liveResults,
    liveSearchInProgress,
    showLiveResults,
    hasLiveQuery,
    onLiveResultNavigate,
  } = props;

  return (
    <div className={keywordSearchWrapperClasses}>
      <button className={css.searchSubmit}>
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>
      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <input
              className={isMobile ? css.mobileInput : css.desktopInput}
              {...input}
              id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              ref={inputRef}
              type="text"
              onChange={e => {
                input.onChange(e);
                onKeywordTyping(e.target.value);
              }}
              placeholder={intl.formatMessage({
                id: 'TopbarSearchForm.placeholder',
              })}
              autoComplete="off"
            />
          );
        }}
      />

      {showLiveResults ? (
        <div className={css.liveResultsPanel}>
          {liveSearchInProgress ? <p className={css.liveResultsMessage}>Searching...</p> : null}

          {!liveSearchInProgress && liveResults.length === 0 && hasLiveQuery ? (
            <p className={css.liveResultsMessage}>No matching results yet.</p>
          ) : null}

          {!liveSearchInProgress && liveResults.length > 0 ? (
            <ul className={css.liveResultsList}>
              {liveResults.map(listing => {
                const title = listing?.attributes?.title || 'Untitled listing';
                const publicData = listing?.attributes?.publicData || {};
                const listingType = publicData.listingType;
                const brandRaw =
                  publicData.BrandName ||
                  publicData.brand ||
                  publicData.brandName ||
                  'Unknown Brand';
                const brandName = String(brandRaw);
                const productTitle = cleanListingTitle(title, brandName);
                const id = listing?.id?.uuid;
                const slug =
                  listingType === 'review'
                    ? publicData.slug || createSlug(title)
                    : createSlug(title);
                const thumbnailUrl = listing?.thumbnailUrl;
                const detailPath =
                  listingType === 'review' ? `/reviews/${slug}/${id}` : `/product/${slug}/${id}`;
                const typeLabel = listingType === 'review' ? 'REVIEW' : 'RESALE';

                if (!id) {
                  return null;
                }

                return (
                  <li key={id} className={css.liveResultsItem}>
                    <Link
                      to={detailPath}
                      className={css.liveResultsLink}
                      onClick={onLiveResultNavigate}
                    >
                      <span className={css.liveResultType}>{typeLabel}</span>
                      <span className={css.liveResultBody}>
                        <span className={css.liveResultThumbWrap} aria-hidden="true">
                          {thumbnailUrl ? (
                            <img src={thumbnailUrl} alt="" className={css.liveResultThumb} />
                          ) : (
                            <span className={css.liveResultThumbPlaceholder} />
                          )}
                        </span>
                        <span className={css.liveResultTextWrap}>
                          <span className={css.liveResultBrand}>{brandName.toUpperCase()}</span>
                          <span className={css.liveResultTitle}>{productTitle}</span>
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        // Merge the standard onChange function with custom behaviur. A better solution would
        // be to use the FormSpy component from Final Form and pass onChange to the
        // onChange prop but that breaks due to insufficient subscription handling.
        // See: https://github.com/final-form/react-final-form/issues/159
        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
          />
        );
      }}
    />
  );
};

/**
 * The main search form for the Topbar.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.desktopInputRoot root class for desktop form input
 * @param {Function} props.onSubmit
 * @param {boolean} props.isMobile
 * @param {Object} props.appConfig
 * @returns {JSX.Element} search form element
 */
const TopbarSearchForm = props => {
  const searchInpuRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const requestSeqRef = useRef(0);
  const [liveQuery, setLiveQuery] = useState('');
  const [liveResults, setLiveResults] = useState([]);
  const [liveSearchInProgress, setLiveSearchInProgress] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const intl = useIntl();
  const { appConfig, onSubmit, onKeywordInputChange, ...restOfProps } = props;

  const onChange = location => {
    if (!isMainSearchTypeKeywords(appConfig) && location.selectedPlace) {
      // Note that we use `onSubmit` instead of the conventional
      // `handleSubmit` prop for submitting. We want to autosubmit
      // when a place is selected, and don't require any extra
      // validations for the form.
      onSubmit({ location });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  const onKeywordSubmit = values => {
    if (isMainSearchTypeKeywords(appConfig)) {
      onSubmit({ keywords: values.keywords });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  const isKeywordsSearch = isMainSearchTypeKeywords(appConfig);
  const submit = isKeywordsSearch ? onKeywordSubmit : onSubmit;

  const trimmedLiveQuery = liveQuery.trim();
  const hasLiveQuery = trimmedLiveQuery.length >= LIVE_SEARCH_MIN_CHARS;
  const showLiveResults = isKeywordsSearch && isInputFocused && hasLiveQuery;

  useEffect(() => {
    if (!isKeywordsSearch) {
      return;
    }

    if (!hasLiveQuery) {
      requestSeqRef.current += 1;
      setLiveResults([]);
      setLiveSearchInProgress(false);
      return;
    }

    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;

    const timerId = window.setTimeout(() => {
      setLiveSearchInProgress(true);

      getSdk()
        .listings.query({
          keywords: trimmedLiveQuery,
          minStock: LIVE_SEARCH_MIN_STOCK,
          stockMode: LIVE_SEARCH_STOCK_MODE,
          perPage: LIVE_SEARCH_LIMIT,
          page: 1,
          include: ['images'],
          'fields.listing': ['title', 'publicData'],
        })
        .then(res => {
          if (requestSeqRef.current !== requestSeq) {
            return;
          }
          const listings = Array.isArray(res?.data?.data) ? res.data.data : [];
          const included = Array.isArray(res?.data?.included) ? res.data.included : [];
          const imageById = new Map(
            included
              .filter(entry => entry?.type === 'image' && entry?.id?.uuid)
              .map(entry => [entry.id.uuid, entry])
          );

          const data = listings.map(listing => {
            const imageId = listing?.relationships?.images?.data?.[0]?.id?.uuid;
            const image = imageId ? imageById.get(imageId) : null;
            const variants = image?.attributes?.variants || {};

            const thumbnailUrl =
              variants['listing-card']?.url ||
              variants['scaled-small']?.url ||
              variants['scaled-medium']?.url ||
              variants.default?.url ||
              null;

            return { ...listing, thumbnailUrl };
          });

          setLiveResults(data);
          setLiveSearchInProgress(false);
        })
        .catch(() => {
          if (requestSeqRef.current !== requestSeq) {
            return;
          }
          setLiveResults([]);
          setLiveSearchInProgress(false);
        });
    }, LIVE_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [hasLiveQuery, isKeywordsSearch, trimmedLiveQuery]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        window.clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  return (
    <FinalForm
      {...restOfProps}
      onSubmit={submit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
        } = formRenderProps;
        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

        // Location search: allow form submit only when the place has changed
        const preventFormSubmit = e => e.preventDefault();
        const submitFormFn = isKeywordsSearch ? handleSubmit : preventFormSubmit;

        const keywordSearchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobile ? css.mobileInputRoot : desktopInputRootClass
        );

        return (
          <Form className={classes} onSubmit={submitFormFn} enforcePagePreloadFor="SearchPage">
            <div
              onFocusCapture={() => {
                if (blurTimeoutRef.current) {
                  window.clearTimeout(blurTimeoutRef.current);
                  blurTimeoutRef.current = null;
                }
                setIsInputFocused(true);
              }}
              onBlurCapture={() => {
                if (blurTimeoutRef.current) {
                  window.clearTimeout(blurTimeoutRef.current);
                }
                blurTimeoutRef.current = window.setTimeout(() => {
                  setIsInputFocused(false);
                }, 120);
              }}
            >
              {isKeywordsSearch ? (
                <KeywordSearchField
                  keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                  iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInpuRef}
                  onKeywordTyping={value => {
                    const nextValue = value || '';
                    setLiveQuery(nextValue);
                    if (onKeywordInputChange) {
                      onKeywordInputChange(nextValue);
                    }
                  }}
                  liveResults={liveResults}
                  liveSearchInProgress={liveSearchInProgress}
                  showLiveResults={showLiveResults}
                  hasLiveQuery={hasLiveQuery}
                  onLiveResultNavigate={() => {
                    setIsInputFocused(false);
                    setLiveResults([]);
                  }}
                />
              ) : (
                <LocationSearchField
                  desktopInputRootClass={desktopInputRootClass}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={searchInpuRef}
                  onLocationChange={onChange}
                />
              )}
            </div>
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
